/**
 * Knowledge Base Editor Component
 * Comprehensive editor for Knowledge Base entities with extensive configuration
 * Tabs: Basic | Connection | Database | Embedding | Business | Search | Status
 */

import { LockIcon, BugIcon, LightbulbIcon, ClipboardIcon, ZapIcon, BarChartIcon } from '@bizfirst/common-react';
import React, { useState, useEffect } from 'react';
import { ModalPortal } from '../../Modals/ModalPortal';
import LookupSelect from '../../Modals/LookupSelect';
import { knowledgeBaseApiClient } from '@flow-studio/api';
import { lookupConfigs } from '../../../lookupConfigs';
import { lookupEventBus } from 'flow-studio-core';
import { useUIStore } from '@flow-studio/store';
import { JsonEditor } from '../../Shared/JsonEditor';
import { AccordionSection } from '../../Shared/AccordionSection';
import { Loader } from '../../Shared/Loader';
import '../Agent/AgentEditor.css'; // Reuse agent editor styles
import './KnowledgeBaseEditor.css';

export interface KnowledgeBaseFormData {
  KnowledgeBaseID?: number;
  AgentID: number | null;
  RagProviderID: number | null;
  Name: string;
  Description: string;
  RagRole: string;
  ConnectionString: string;
  IsSse: boolean;
  IsHttps: boolean;
  ApiKey: string;
  ApiEndpoint: string;
  ApiVersion: string;
  DatabaseName: string;
  PrimaryCollectionName: string;
  FallbackCollectionName: string;
  VectorDbProvider: string;
  GraphDbProvider: string;
  EmbeddingModelID: number | null;
  EmbeddingDimension: number | null;
  BusinessDomainID: number | null;
  BusinessCategoryID: number | null;
  BusinessClassificationID: number | null;
  BusinessSectorID: number | null;
  BusinessIndustryID: number | null;
  BusinessProfileID: number | null;
  MaxResults: number | null;
  SimilarityThreshold: number | null;
  Configuration: string; // JSON string
  IsDefault: boolean;
  IsActive: boolean;
  Priority: number;
  ResID?: string;
  CreatedOn?: string;
  CreatedBy?: string;
  LastModifiedOn?: string;
  LastModifiedBy?: string;
}

interface KnowledgeBaseEditorProps {
  kbId?: number;
  isOpen: boolean;
  onClose: () => void;
  onKBSaved?: (kb: KnowledgeBaseFormData) => void;
}

type KBTab = 'basic' | 'connection' | 'database' | 'embedding' | 'business' | 'search' | 'status' | 'security' | 'debugger';

const defaultFormData: KnowledgeBaseFormData = {
  AgentID: null,
  RagProviderID: null,
  Name: '',
  Description: '',
  RagRole: '',
  ConnectionString: '',
  IsSse: false,
  IsHttps: true,
  ApiKey: '',
  ApiEndpoint: '',
  ApiVersion: '',
  DatabaseName: '',
  PrimaryCollectionName: '',
  FallbackCollectionName: '',
  VectorDbProvider: '',
  GraphDbProvider: '',
  EmbeddingModelID: null,
  EmbeddingDimension: null,
  BusinessDomainID: null,
  BusinessCategoryID: null,
  BusinessClassificationID: null,
  BusinessSectorID: null,
  BusinessIndustryID: null,
  BusinessProfileID: null,
  MaxResults: 10,
  SimilarityThreshold: 0.7,
  Configuration: '{}',
  IsDefault: false,
  IsActive: true,
  Priority: 0,
};

export const KnowledgeBaseEditor: React.FC<KnowledgeBaseEditorProps> = ({
  kbId,
  isOpen,
  onClose,
  onKBSaved,
}) => {
  const { showToast } = useUIStore();
  const [activeTab, setActiveTab] = useState<KBTab>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState<KnowledgeBaseFormData>(defaultFormData);

  // Load KB on open
  useEffect(() => {
    if (isOpen) {
      if (kbId) {
        loadKBData();
        setIsEditing(false);
      } else {
        resetForm();
        setIsEditing(true);
      }
    }
  }, [isOpen, kbId]);

  const loadKBData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await knowledgeBaseApiClient.getById({ id: kbId! });
      if (response.success && response.data) {
        setFormData(response.data as KnowledgeBaseFormData);
      } else {
        setError(response.error?.message || 'Failed to load Knowledge Base');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading Knowledge Base');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setError(null);
    setIsDirty(false);
  };

  const handleRefresh = async () => {
    if (kbId) {
      await loadKBData();
    }
  };

  const handleChange = (field: keyof KnowledgeBaseFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.Name.trim()) {
      setError('Knowledge Base name is required');
      return;
    }

    setLoading(true);
    try {
      const response = kbId
        ? await knowledgeBaseApiClient.update(kbId, formData)
        : await knowledgeBaseApiClient.create(formData);

      if (response.success && response.data) {
        //  Auto-refresh related lookups
        if (!kbId) {
          lookupEventBus.emitMultiple([
            'lookup_agent',
            'lookup_ragProvider',
            'lookup_embeddingModel',
            'lookup_businessDomain',
            'lookup_businessCategory',
            'lookup_businessClassification',
            'lookup_businessSector',
            'lookup_businessIndustry',
            'lookup_businessProfile',
          ]);
        }

        onKBSaved?.(response.data as KnowledgeBaseFormData);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to save Knowledge Base');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving Knowledge Base');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      showToast('info', 'Changes discarded');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div className="agent-editor-overlay" onClick={handleCancel}>
        <div
          className="agent-editor-container"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="agent-editor-header">
            <h1 className="editor-title">
              {kbId ? 'Edit Knowledge Base' : 'Create Knowledge Base'}
            </h1>
            <div className="editor-header-actions">
              <button className="close-btn" onClick={handleCancel}>
                ✕
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="editor-error-banner">
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && !kbId && (
            <div className="editor-loading">
              <Loader label="Loading..." />
            </div>
          )}

          {/* Content */}
          {!loading && (
            <form onSubmit={handleSave} className="editor-form">
              {/* Tabs */}
              <div className="top-tabs">
                {(['basic', 'connection', 'database', 'embedding', 'business', 'search', 'status', 'security', 'debugger'] as KBTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'basic' && 'Basic'}
                    {tab === 'connection' && 'Connection'}
                    {tab === 'database' && 'Database'}
                    {tab === 'embedding' && 'Embedding'}
                    {tab === 'business' && 'Business'}
                    {tab === 'search' && 'Search'}
                    {tab === 'status' && 'Status'}
                    {tab === 'security' && <><LockIcon size={12} /> Security</>}
                    {tab === 'debugger' && <><BugIcon size={12} /> Debugger</>}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Basic Tab */}
                {activeTab === 'basic' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={formData.Name}
                        onChange={(e) => handleChange('Name', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., Product Documentation"
                        maxLength={200}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Description</label>
                      <textarea
                        value={formData.Description}
                        onChange={(e) => handleChange('Description', e.target.value)}
                        disabled={!isEditing}
                        placeholder="What information does this knowledge base contain?"
                        rows={3}
                        maxLength={1000}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.agent}
                        value={formData.AgentID}
                        onChange={(value) => handleChange('AgentID', value)}
                        label="Agent"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select agent (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.ragProvider}
                        value={formData.RagProviderID}
                        onChange={(value) => handleChange('RagProviderID', value)}
                        label="RAG Provider"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select RAG provider (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <label>RAG Role</label>
                      <input
                        type="text"
                        value={formData.RagRole}
                        onChange={(e) => handleChange('RagRole', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., admin, user"
                        maxLength={100}
                      />
                    </div>
                  </div>
                )}

                {/* Connection Tab */}
                {activeTab === 'connection' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Connection String</label>
                      <input
                        type="text"
                        value={formData.ConnectionString}
                        onChange={(e) => handleChange('ConnectionString', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Database connection string"
                        maxLength={1000}
                      />
                    </div>

                    <div className="form-group full">
                      <label>API Endpoint</label>
                      <input
                        type="text"
                        value={formData.ApiEndpoint}
                        onChange={(e) => handleChange('ApiEndpoint', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://api.example.com/v1"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group">
                      <label>API Version</label>
                      <input
                        type="text"
                        value={formData.ApiVersion}
                        onChange={(e) => handleChange('ApiVersion', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., 2023-05-15"
                        maxLength={50}
                      />
                    </div>

                    <div className="form-group full">
                      <label>API Key</label>
                      <input
                        type="password"
                        value={formData.ApiKey}
                        onChange={(e) => handleChange('ApiKey', e.target.value)}
                        disabled={!isEditing}
                        placeholder="API key (encrypted)"
                        maxLength={500}
                      />
                    </div>

                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.IsHttps}
                          onChange={(e) => handleChange('IsHttps', e.target.checked)}
                          disabled={!isEditing}
                        />
                        <span>Use HTTPS</span>
                      </label>

                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.IsSse}
                          onChange={(e) => handleChange('IsSse', e.target.checked)}
                          disabled={!isEditing}
                        />
                        <span>Server-Sent Events</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Database Tab */}
                {activeTab === 'database' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Database Name</label>
                      <input
                        type="text"
                        value={formData.DatabaseName}
                        onChange={(e) => handleChange('DatabaseName', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., documents_db"
                        maxLength={200}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Primary Collection Name</label>
                      <input
                        type="text"
                        value={formData.PrimaryCollectionName}
                        onChange={(e) =>
                          handleChange('PrimaryCollectionName', e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="e.g., documents"
                        maxLength={200}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Fallback Collection Name</label>
                      <input
                        type="text"
                        value={formData.FallbackCollectionName}
                        onChange={(e) =>
                          handleChange('FallbackCollectionName', e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="Optional: Fallback collection"
                        maxLength={200}
                      />
                    </div>

                    <div className="form-group">
                      <label>Vector DB Provider</label>
                      <input
                        type="text"
                        value={formData.VectorDbProvider}
                        onChange={(e) => handleChange('VectorDbProvider', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., Pinecone, Weaviate"
                        maxLength={200}
                      />
                    </div>

                    <div className="form-group">
                      <label>Graph DB Provider</label>
                      <input
                        type="text"
                        value={formData.GraphDbProvider}
                        onChange={(e) => handleChange('GraphDbProvider', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., Neo4j, ArangoDB"
                        maxLength={200}
                      />
                    </div>
                  </div>
                )}

                {/* Embedding Tab */}
                {activeTab === 'embedding' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.embeddingModel}
                        value={formData.EmbeddingModelID}
                        onChange={(value) => handleChange('EmbeddingModelID', value)}
                        label="Embedding Model"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select embedding model (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <label>Embedding Dimension</label>
                      <input
                        type="number"
                        value={formData.EmbeddingDimension ?? ''}
                        onChange={(e) =>
                          handleChange(
                            'EmbeddingDimension',
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        disabled={!isEditing}
                        placeholder="e.g., 1536"
                      />
                    </div>
                  </div>
                )}

                {/* Business Tab */}
                {activeTab === 'business' && (
                  <div className="form-grid">
                    <AccordionSection
                      title="Business Classification"
                      defaultExpanded={true}
                      icon={<BarChartIcon size={12} />}
                    >
                      <div className="form-grid">
                        <div className="form-group">
                          <LookupSelect
                            fetchConfig={lookupConfigs.businessDomain}
                            value={formData.BusinessDomainID}
                            onChange={(value) => {
                              handleChange('BusinessDomainID', value);
                              handleChange('BusinessCategoryID', null);
                              handleChange('BusinessClassificationID', null);
                            }}
                            label="Business Domain"
                            required={false}
                            disabled={!isEditing}
                            placeholder="Select domain (optional)..."
                            searchable={true}
                            maxItemsInDropdown={50}
                          />
                        </div>

                        <div className="form-group">
                          <LookupSelect
                            fetchConfig={lookupConfigs.businessCategory}
                            value={formData.BusinessCategoryID}
                            onChange={(value) => {
                              handleChange('BusinessCategoryID', value);
                              handleChange('BusinessClassificationID', null);
                            }}
                            label="Business Category"
                            required={false}
                            disabled={!isEditing || !formData.BusinessDomainID}
                            placeholder="Select category (optional)..."
                            searchable={true}
                            maxItemsInDropdown={50}
                          />
                        </div>

                        <div className="form-group">
                          <LookupSelect
                            fetchConfig={lookupConfigs.businessClassification}
                            value={formData.BusinessClassificationID}
                            onChange={(value) => handleChange('BusinessClassificationID', value)}
                            label="Business Classification"
                            required={false}
                            disabled={!isEditing || !formData.BusinessCategoryID}
                            placeholder="Select classification (optional)..."
                            searchable={true}
                            maxItemsInDropdown={50}
                          />
                        </div>

                        <div className="form-group">
                          <LookupSelect
                            fetchConfig={lookupConfigs.businessSector}
                            value={formData.BusinessSectorID}
                            onChange={(value) => handleChange('BusinessSectorID', value)}
                            label="Business Sector"
                            required={false}
                            disabled={!isEditing}
                            placeholder="Select sector (optional)..."
                            searchable={true}
                            maxItemsInDropdown={50}
                          />
                        </div>

                        <div className="form-group">
                          <LookupSelect
                            fetchConfig={lookupConfigs.businessIndustry}
                            value={formData.BusinessIndustryID}
                            onChange={(value) => handleChange('BusinessIndustryID', value)}
                            label="Business Industry"
                            required={false}
                            disabled={!isEditing}
                            placeholder="Select industry (optional)..."
                            searchable={true}
                            maxItemsInDropdown={50}
                          />
                        </div>

                        <div className="form-group">
                          <LookupSelect
                            fetchConfig={lookupConfigs.businessProfile}
                            value={formData.BusinessProfileID}
                            onChange={(value) => handleChange('BusinessProfileID', value)}
                            label="Business Profile"
                            required={false}
                            disabled={!isEditing}
                            placeholder="Select profile (optional)..."
                            searchable={true}
                            maxItemsInDropdown={50}
                          />
                        </div>
                      </div>
                    </AccordionSection>
                  </div>
                )}

                {/* Search Tab */}
                {activeTab === 'search' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Max Results</label>
                      <input
                        type="number"
                        value={formData.MaxResults ?? ''}
                        onChange={(e) =>
                          handleChange(
                            'MaxResults',
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        disabled={!isEditing}
                        placeholder="10"
                        min="1"
                      />
                    </div>

                    <div className="form-group">
                      <label>Similarity Threshold (0-1)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={formData.SimilarityThreshold ?? ''}
                        onChange={(e) =>
                          handleChange(
                            'SimilarityThreshold',
                            e.target.value ? parseFloat(e.target.value) : null
                          )
                        }
                        disabled={!isEditing}
                        placeholder="0.7"
                      />
                    </div>

                    <div className="form-group full">
                      <JsonEditor
                        label="Search Configuration (JSON)"
                        value={formData.Configuration}
                        onChange={(value) => handleChange('Configuration', value)}
                        disabled={!isEditing}
                        height="250px"
                      />
                    </div>
                  </div>
                )}

                {/* Status Tab */}
                {activeTab === 'status' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Priority</label>
                      <input
                        type="number"
                        value={formData.Priority}
                        onChange={(e) =>
                          handleChange('Priority', parseInt(e.target.value) || 0)
                        }
                        disabled={!isEditing}
                        placeholder="0"
                      />
                    </div>

                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.IsActive}
                          onChange={(e) => handleChange('IsActive', e.target.checked)}
                          disabled={!isEditing}
                        />
                        <span>Active</span>
                      </label>

                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.IsDefault}
                          onChange={(e) => handleChange('IsDefault', e.target.checked)}
                          disabled={!isEditing}
                        />
                        <span>Set as Default</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div className="form-grid read-only">
                    <div className="form-group">
                      <label>Primary ID</label>
                      <div className="display-value">{formData.KnowledgeBaseID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Resource ID (ResID)</label>
                      <div className="display-value">{formData.ResID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Created On</label>
                      <div className="display-value">
                        {formData.CreatedOn ? new Date(formData.CreatedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Created By</label>
                      <div className="display-value">{formData.CreatedBy || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified On</label>
                      <div className="display-value">
                        {formData.LastModifiedOn ? new Date(formData.LastModifiedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified By</label>
                      <div className="display-value">{formData.LastModifiedBy || 'N/A'}</div>
                    </div>
                  </div>
                )}

                {/* Debugger Tab */}
                {activeTab === 'debugger' && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#f8fafc', fontSize: '14px' }}><ClipboardIcon size={12} /> Entity Data (JSON)</h4>
                      <textarea
                        value={JSON.stringify(formData, null, 2)}
                        readOnly
                        style={{
                          width: '100%',
                          height: '400px',
                          padding: '12px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(58, 140, 69, 0.3)',
                          borderRadius: '6px',
                          color: '#3A8C45',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          resize: 'vertical',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      <LightbulbIcon size={12} /> Tip: Copy this JSON data for debugging or API requests
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="editor-footer">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        resetForm();
                        if (kbId) loadKBData();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    {kbId && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRefresh}
                        disabled={loading}
                        title="Refresh data from server"
                      >
                        <ZapIcon size={14} /> Refresh
                      </button>
                    )}
                    {kbId && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                        title="Enable edit mode"
                      >
                        Edit
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default KnowledgeBaseEditor;
